import {
    ChangeDetectionStrategy,
    Component,
    Injector,
    Input,
    OnInit,
    ViewChild,
    ViewEncapsulation,
    computed,
    inject,
    input,
    signal,
} from '@angular/core';
import { FiRemoteStateFullDataSource } from '@fi-shared/fi-items-list/fi-remote-state-full-data-source';
import {
    IItemsListAction2,
    IItemsListDataFetchResult,
    IListViewTag,
    ISideListOptionsItem,
} from '@fi-shared/fi-items-list/fi-items-list';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { ModuleType, ObjectTypes, SideListOptionType } from '@fi-shared/types';
import { takeUntil } from 'rxjs/operators';
import { UserService } from '@fi-app/user/user.service';
import { StatisticService } from '@fi-shared/statistic/statistic.service';
import { FiTagsListPositionTypes, FiTagsSelectionTypes } from '@fi-app/components/items-list/enums';
import { ListViewSettingModel } from '@fi-app/components/items-list/list-view-setting.model';
import { ILightWeightObject, UserParameterModel } from '@fi-shared/models';
import { ILoadDataArgs } from '@fi-shared/fi-items-list/fi-data-source-base.directive';
import { FiProfileSizes } from '@fi-shared/profile-icon/profile-icon.component';
import { FiItemsDeleteHelperService } from '@fi-shared/fi-shared-services/fi-items-delete-helper.service';
import { INameIdObj } from '@fi-shared/fi-shared-interfaces';
import { FiDisplayType } from '@fi-shared/types/display-type';
import { cloneDeep, isDefined } from '@fi-shared/utils';
import { IContactModel } from '@fi-shared/models/common';
import { FiUserListTableComponent } from '@fi-app/user/user-list/fi-user-list/fi-user-list-table/fi-user-list-table.component';
import { FiItemsListType } from '@fi-shared/fi-items-list/fi-items-list-type';
import { IUserListItemModel, IUserListItemViewModel } from '@fi-app/user/user';

@Component({
    selector: 'fi-user-list',
    templateUrl: './fi-user-list.component.html',
    styleUrls: ['./fi-user-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-user-list', '[class.no-side-padding]': 'isTagsSidebarShown()' },
    standalone: false,
})
export class FiUserListComponent extends FiRemoteStateFullDataSource<IUserListItemViewModel> implements OnInit {
    public isSideListShown = input<boolean>(true);
    hideActions = input<boolean>(false);

    @Input() userListViewSettings: ListViewSettingModel;
    @Input() tagId: number;
    @Input() hasCheckedColumn: boolean = true;
    @Input() listDisplayType: FiDisplayType;
    @Input() onlyWithContactPhones: boolean = false;
    @Input() isRemarkColumnShown: boolean = true;
    @Input() isEmailColumnShown: boolean = false;
    @Input() isPhoneContactColumnShown: boolean = false;
    @Input() isNestedList: boolean = false;
    @Input() textMode = false;

    @ViewChild(FiUserListTableComponent) userListTableComponent: FiUserListTableComponent;

    private stateAccessCheckService = inject(StateAccessCheckService);
    private userService = inject(UserService);
    private statisticService = inject(StatisticService);
    private fiItemsDeleteHelperService = inject(FiItemsDeleteHelperService);

    public readonly defaultSortColumnName: string = 'FullName';
    public readonly defaultSortDirection: boolean = false;
    public objectType: ObjectTypes = ObjectTypes.User;
    public itemsListType = FiItemsListType.Colleagues;

    protected readonly userAccessRight = signal<boolean>(
        this.stateAccessCheckService.isStateAllowedForCurrentUser(FiStateNames.User.Edit),
    );
    protected optionsTags = signal<ISideListOptionsItem[]>([]);
    protected readonly userFilters: ISideListOptionsItem[] = [
        {
            value: SideListOptionType.SharedWithMe,
            text: this.appTextService.get('all_colleagues'),
        },
    ];
    protected itemsListActions: IItemsListAction2[] = [];

    protected showTopActions = computed(
        () => this.userAccessRight() && !this.hideActions() && !this.extendedSearchMode,
    );

    protected isTagsSidebarShown = computed(
        () => this.isSidelistVisible() && this.userListViewSettings.PositionType === FiTagsListPositionTypes.Left,
    );
    protected isTagsHorizontalListShown = computed(
        () => this.isSidelistVisible() && this.userListViewSettings.PositionType === FiTagsListPositionTypes.Top,
    );

    private isSidelistVisible = computed(() => this.isSideListShown() && !!this.optionsTags().length);

    // note: it is not changed anywhere
    private includeInactiveUsers: boolean = true;

    constructor(injector: Injector) {
        super(injector);
    }

    ngOnInit() {
        this.setListDataSettings();
        this.initSideList();
    }

    ngAfterViewInit(): void {
        this.matSort = this.userListTableComponent?.matSort;
    }

    private setListDataSettings() {
        if (isDefined(this.listDisplayType)) {
            this.currentListDisplayType = this.listDisplayType;
        } else {
            this.setCurrentListDisplayType();
            this.subscribeOnListDisplayTypeChange();
        }

        this.selectedTagId = this.tagId;
        this.statisticService.Visit(ModuleType.Core);
        this.itemsListActions = [
            {
                title: this.appTextService.get('common.delete'),
                onClick: () => this.showUserDeleteConfirmationDialog(),
            },
        ];
        this.userService.onRemoveUserFromList$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.reloadData();
            this.cdr.markForCheck();
        });
    }

    private initSideList() {
        if (!this.isSideListShown()) {
            return;
        }

        if (this.userListViewSettings.ListType === FiTagsSelectionTypes.SelectedTags) {
            // todo: add sortIndex to ListViewTags items since in customer site design
            //  settings list view editor can be set sortBy FiAppearanceListViewEditComponent
            this.optionsTags.set(
                this.userListViewSettings.ListViewTags.map((listViewTag: IListViewTag) => {
                    return { value: listViewTag.Id, text: listViewTag.Name };
                }),
            );
            return;
        }

        this.userService.getUserTags().then((userTags: ILightWeightObject[]) => {
            this.optionsTags.set(userTags.map((tag: ILightWeightObject) => ({ value: tag.Id, text: tag.Name })));
        });
    }

    public loadListDataFn(loadArgs: ILoadDataArgs): Promise<IItemsListDataFetchResult<IUserListItemViewModel>> {
        let parameter: UserParameterModel = new UserParameterModel();

        parameter = Object.assign(parameter, this.getItemsListParameter(loadArgs));
        parameter.IncludeInactiveUsers = this.includeInactiveUsers;
        if (isDefined(this.selectedTagId)) {
            parameter.TagIds = [this.selectedTagId];
        }
        parameter.FetchParams = {
            FetchItems: true,
            FetchTotalCount: !loadArgs.fetchOnlyItems,
            FetchLightWeightObjects: !loadArgs.fetchOnlyItems,
        };
        parameter.OnlyWithContactPhones = this.onlyWithContactPhones;
        return this.isPhoneContactColumnShown
            ? this.getUsersContactData(parameter)
            : (this.userService.getUsersListData(parameter) as Promise<
                  IItemsListDataFetchResult<IUserListItemViewModel>
              >);
    }

    private getUsersContactData(parameter: UserParameterModel) {
        return new Promise<IItemsListDataFetchResult<IUserListItemViewModel>>((resolve, reject) => {
            this.userService
                .getUsersListData(parameter)
                .then((fetchResult: IItemsListDataFetchResult<IUserListItemModel>) => {
                    resolve({
                        Items: this.convertUserModelsToUserContacts(fetchResult.Items),
                        TotalCount: fetchResult.TotalCount,
                        LightWeightObjectsForLocalSearch: fetchResult.LightWeightObjectsForLocalSearch,
                    });
                }, reject);
        });
    }

    private convertUserModelsToUserContacts(users: IUserListItemModel[]): IUserListItemViewModel[] {
        return users.map((user: IUserListItemModel) => {
            const defaultContact: IContactModel = user.PhoneNumberContacts.find(
                (item: IContactModel) => item.IsDefault,
            );
            const contactToAdd: IUserListItemViewModel = Object.assign(cloneDeep(user), defaultContact);
            contactToAdd.ChildItems = [];

            if (user.PhoneNumberContacts.length > 1) {
                contactToAdd.ChildItems = user.PhoneNumberContacts.map((contact: IContactModel) => {
                    const childUserContact: IUserListItemViewModel = Object.assign(cloneDeep(user), contact);
                    childUserContact.ParentId = defaultContact.Id;
                    return childUserContact;
                });
            }

            return contactToAdd;
        });
    }

    private showUserDeleteConfirmationDialog(): void {
        // user can't delete himself
        // https://chainformation.atlassian.net/browse/FI-2249
        if (this.isCurrentUserSelected()) {
            this.userService.showCantDeleteCurrentUserDialog();
            return;
        }

        const selectedItems = this.selectedItems();
        this.fiItemsDeleteHelperService.userDeleteConfirm(selectedItems).then(() => {
            const selectedItemIds = selectedItems.map((selectedItem: IUserListItemModel) => selectedItem.Id);
            this.userService.deleteUsers(selectedItemIds).then(() => {
                this.fiItemsDeleteHelperService.showDeletedObjectSimpleSnackBar(
                    ObjectTypes.User,
                    selectedItemIds,
                    selectedItems[0].FullName,
                );
                this.reloadData();
            });
        });
    }

    private isCurrentUserSelected(): boolean {
        return !!this.selectedItems().find((user: IUserListItemModel) => {
            return user.Id === this.userDataService.getUserId();
        });
    }

    protected getUserRolesAnchors(user: IUserListItemModel): INameIdObj[] {
        return user.Roles.map((role: ILightWeightObject) => {
            return { Name: role.Name, Id: role.Id };
        });
    }

    protected navigateToRole(roleId: number): void {
        this.fiStateService.go(FiStateNames.Role.View, { Id: roleId });
    }

    protected openUserViewDialog($event: MouseEvent, userId: number): void {
        $event.stopPropagation();
        this.userService.openUserViewDialog(userId);
    }

    protected readonly FiProfileSizes = FiProfileSizes;
}
